/**
 * useOutreachTemplates — the campaign's visible template library (its own
 * rows, its brand's and the global ones) plus the four write actions.
 *
 * Every write goes through the CAMPAIGN-scoped path: a brand-scoped row is
 * shared with other campaigns, so editing or archiving one needs
 * `confirmBrand` (the BE answers 409 without it, carrying the used-by count).
 * `preview` is a read modelled as a mutation — it renders step 1 against a
 * chosen roster entry on demand, so it has no cache slot of its own.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  OutreachFillStepRequest,
  OutreachFillStepResult,
  OutreachTemplate,
  OutreachTemplateGroups,
  OutreachTemplateInput,
  OutreachTemplatePatch,
  OutreachTemplatePreview,
} from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import {
  archiveOutreachTemplate,
  createOutreachTemplate,
  fillOutreachTemplateStep,
  fetchOutreachTemplates,
  patchOutreachTemplate,
  previewOutreachTemplate,
  restoreOutreachTemplate,
} from '../api/outreachApi';

export function useOutreachTemplates(campaignId: string) {
  return useQuery<OutreachTemplateGroups>({
    queryKey: campaignKeys.outreachTemplates(campaignId),
    queryFn: () => fetchOutreachTemplates(campaignId),
    enabled: !!campaignId,
    staleTime: 30_000,
  });
}

export interface PatchTemplateVars {
  templateId: number;
  patch: OutreachTemplatePatch;
  confirmBrand?: boolean;
}

export interface ArchiveTemplateVars {
  templateId: number;
  confirmBrand?: boolean;
}

export interface PreviewTemplateVars {
  templateId: number;
  entryId: string;
}

export function useOutreachTemplateMutations(campaignId: string) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachTemplates(campaignId) });
  };

  const create = useMutation<OutreachTemplate, Error, OutreachTemplateInput>({
    mutationFn: (input) => createOutreachTemplate(campaignId, input),
    onSuccess: invalidate,
  });

  const patch = useMutation<OutreachTemplate, Error, PatchTemplateVars>({
    mutationFn: (vars) =>
      patchOutreachTemplate(campaignId, vars.templateId, vars.patch, { confirmBrand: vars.confirmBrand }),
    onSuccess: invalidate,
  });

  const archive = useMutation<void, Error, ArchiveTemplateVars>({
    mutationFn: (vars) => archiveOutreachTemplate(campaignId, vars.templateId, { confirmBrand: vars.confirmBrand }),
    onSuccess: invalidate,
  });

  const restore = useMutation<void, Error, ArchiveTemplateVars>({
    mutationFn: (vars) => restoreOutreachTemplate(campaignId, vars.templateId, { confirmBrand: vars.confirmBrand }),
    onSuccess: invalidate,
  });

  const preview = useMutation<OutreachTemplatePreview, Error, PreviewTemplateVars>({
    mutationFn: (vars) => previewOutreachTemplate(campaignId, vars.templateId, vars.entryId),
  });

  // A read modelled as a mutation, like preview: the AI writes one step and nothing is stored until the template is saved.
  const fillStep = useMutation<OutreachFillStepResult, Error, OutreachFillStepRequest>({
    mutationFn: (req) => fillOutreachTemplateStep(campaignId, req),
  });

  return { create, patch, archive, restore, preview, fillStep };
}
